/**
 * Location utility functions for managing user location in localStorage
 */

/**
 * Save user location to localStorage
 * @param {Object} location - Location object with latitude and longitude
 */
export const saveUserLocation = (location) => {
  try {
    if (location && location.latitude && location.longitude) {
      localStorage.setItem('userLocation', JSON.stringify({
        latitude: location.latitude,
        longitude: location.longitude,
        timestamp: new Date().toISOString()
      }));
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('locationUpdated'));
    }
  } catch (error) {
    console.error('Error saving user location:', error);
  }
};

/**
 * Get user location from localStorage
 * @returns {Object|null} Location object with latitude and longitude, or null
 */
export const getUserLocation = () => {
  try {
    const savedLocation = localStorage.getItem('userLocation');
    console.log('getUserLocation - Raw localStorage value:', savedLocation);
    
    if (savedLocation) {
      const location = JSON.parse(savedLocation);
      console.log('getUserLocation - Parsed location:', location);
      
      // Check if location is less than 24 hours old (optional: refresh if too old)
      const locationAge = Date.now() - new Date(location.timestamp).getTime();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      console.log('getUserLocation - Location age:', locationAge, 'ms (max:', maxAge, 'ms)');
      
      if (locationAge < maxAge) {
        console.log('getUserLocation - Returning valid location:', {
          latitude: location.latitude,
          longitude: location.longitude
        });
        return {
          latitude: location.latitude,
          longitude: location.longitude
        };
      } else {
        // Location is too old, remove it
        console.log('getUserLocation - Location too old, removing');
        localStorage.removeItem('userLocation');
        return null;
      }
    } else {
      console.log('getUserLocation - No saved location found in localStorage');
    }
  } catch (error) {
    console.error('Error loading user location:', error);
  }
  return null;
};

/**
 * Clear user location from localStorage
 */
export const clearUserLocation = () => {
  localStorage.removeItem('userLocation');
};

/**
 * Map Nominatim state / ISO code to our province value.
 */
function mapStateToProvince(stateRaw, iso3166) {
  // Nepal ISO3166-2: NP-P1, NP-P2, NP-P3 (Bagmati), NP-P4 (Gandaki), etc.
  if (iso3166) {
    const code = String(iso3166).toUpperCase();
    if (code === 'NP-P3') return 'Bagmati';
    if (code === 'NP-P4') return 'Gandaki';
    if (code === 'NP-P5') return 'Lumbini';
    if (code === 'NP-P6') return 'Karnali';
    if (code === 'NP-P7') return 'Sudurpashchim';
    if (code === 'NP-P1') return 'Koshi';
    if (code === 'NP-P2') return 'Madhesh';
  }
  if (!stateRaw || typeof stateRaw !== 'string') return '';
  const s = stateRaw.toLowerCase();
  if (s.includes('bagmati') || s.includes('bagamati') || s.includes('province 3') || s.includes('p3')) return 'Bagmati';
  if (s.includes('gandaki') || s.includes('province 4') || s.includes('p4')) return 'Gandaki';
  if (s.includes('lumbini') || s.includes('province 5') || s.includes('p5')) return 'Lumbini';
  if (s.includes('karnali') || s.includes('province 6') || s.includes('p6')) return 'Karnali';
  if (s.includes('sudurpashchim') || s.includes('sudurpaschhim') || s.includes('province 7') || s.includes('p7')) return 'Sudurpashchim';
  if (s.includes('koshi') || s.includes('province 1') || s.includes('p1')) return 'Koshi';
  if (s.includes('madhesh') || s.includes('province 2') || s.includes('p2')) return 'Madhesh';
  return stateRaw.trim();
}

/**
 * Normalize district name to match our dropdown (e.g. "Kathmandu District" -> "Kathmandu").
 */
function normalizeDistrict(name) {
  if (!name || typeof name !== 'string') return '';
  return name.replace(/\s*district\s*$/i, '').trim();
}

/**
 * Reverse geocode coordinates to address using Nominatim (OpenStreetMap).
 * Returns fields suitable for profile: city, province, district, municipality, address (street).
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Promise<{ city: string, province: string, district: string, municipality: string, address: string }>}
 */
export const reverseGeocode = async (lat, lon) => {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1&zoom=18`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  const res = await fetch(url, {
    headers: { Accept: 'application/json', 'User-Agent': 'TajaBazzar/1.0' },
    signal: controller.signal
  });
  clearTimeout(timeoutId);
  if (!res.ok) throw new Error('Reverse geocode failed');
  const data = await res.json();
  const addr = data.address || {};
  const displayName = data.display_name || '';

  const stateRaw = addr.state || '';
  const iso3166 = addr['ISO3166-2-lvl4'] || '';
  const province = mapStateToProvince(stateRaw, iso3166);

  // District: county is usually the district in Nepal. Normalize to match dropdown.
  const districtRaw = addr.county || addr.state_district || addr.district || addr.city_district || '';
  const district = normalizeDistrict(typeof districtRaw === 'string' ? districtRaw : '');

  // City
  const city = String(addr.city || addr.town || addr.village || addr.municipality || addr.county || '').trim();

  // Municipality
  const municipality = String(addr.municipality || addr.city || addr.town || addr.village || addr.county || '').trim();

  // Street / address: collect all relevant parts
  const streetParts = [
    addr.house_number,
    addr.road,
    addr.street,
    addr.footway,
    addr.pedestrian,
    addr.neighbourhood,
    addr.suburb
  ].filter(Boolean);
  let address = streetParts.join(', ').trim() || (addr.road || addr.street || '').trim();

  // Fallback: use start of display_name (often "Place, Road, Area...") as address
  if (!address && displayName) {
    const parts = displayName.split(',').map(p => p.trim());
    const knownEnd = [addr.county, addr.state, addr.country].filter(Boolean);
    const take = parts.filter(p => !knownEnd.some(e => p === e)).slice(0, 3);
    address = take.join(', ');
  }

  return {
    city,
    province,
    district,
    municipality,
    address: address || '',
    displayName: displayName || ''
  };
};

/**
 * Get current location using browser geolocation API
 * @returns {Promise<Object>} Location object with latitude and longitude
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        // Save to localStorage
        saveUserLocation(location);
        resolve(location);
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000 // Accept cached location up to 1 minute old
      }
    );
  });
};

